import {
    type CSSResult,
    css,
    html,
    LitElement,
    type TemplateResult,
} from "lit";
import { property, state } from "lit/decorators.js";
import { register, type SwiperContainer } from "swiper/element/bundle";
import type { Cart, CartItem, GWPPromotion } from "../types/cart.ts";
import { GWPTypes } from "../types/cart.ts";
import {
    IconAdded,
    IconChevronDown,
    IconChevronUp,
    IconLock,
    IconSwiperArrow,
} from "./icons.ts";

interface Props {
    locales: {
        free_gift_spend_amount_heading: string;
        free_gift_spend_amount_full: string;
        free_gift_spend_amount_remain: string;
        select_free_gift_heading: string;
        choose_free_gift_heading: string;
        gift_added_to_cart_heading: string;
    };
}

class GWPSelector extends LitElement {
    @property({ type: Object })
    public props!: Props;

    @state()
    private cart: Cart;

    @state()
    private cartTotalPrice = 0; // Cart total price excluding gifts

    @state()
    private hasFreeGift = false; // For gift notifications

    @state()
    private thresholds: number[] = [];

    @state()
    private firstThreshold = 0;

    @state()
    private lastThreshold = 0;

    @state()
    private valueUntilNextGift = 0;

    private gwpPromotions: GWPPromotion[]; // All promotions except for the 'spend X get Y' ones
    private sxgyPromotions: GWPPromotion[]; // "Spend X get Y" promotions

    static styles: CSSResult = css`
    details {
      border-bottom: 1px solid #BFBFBF;
      padding: 20px;

      summary {
        display: flex;
        flex-direction: column;

        .accordion-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
          cursor: pointer;

          h4 {
            display: flex;
            font-size: 18px;
            line-height: 24px;
            font-weight: 500;
            margin: 0 0 4px 0;
          }

          .icon-chevron--up {
            display: none;
          }

          .icon-chevron--down {
            display: block;
          }
        }

        .gwp-range {
          padding-bottom: 10px;
        }

        .gwp-description {
          font-size: 15px;
          line-height: 16px;
          font-weight: 400;
          margin: 0;
        }

        &::marker {
          content: "";
        }
      }

      &[open] {
        background-color: #F5F5F5;

        summary {
          .accordion-header {
            .icon-chevron--up {
              display: block;
            }

            .icon-chevron--down {
              display: none;
            }
          }
        }
      }
    }

    .progress-wrapper {
      display: flex;
      padding-top: 16px;

      progress {
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        border: none;
        width: 100%;
        height: 7px;
        background-color: #BFBFBF;
        color: #000000;
        border-radius: 4px;
        margin: 0 4px;
      }

      progress[value]::-webkit-progress-bar {
        background: #BFBFBF;
        border: none;
        border-radius: 4px;
      }

      progress[value]::-webkit-progress-value {
        background: #000000;
        border: none;
        border-radius: 4px;
        transition: width 1s ease-in-out;
      }
    }

    .progress-indicators {
      width: 100%;
      position: relative;
      margin-top: -8px;

      .progress-indicator {
        position: absolute;
        height: 9px;
        width: 9px;

        .progress-indicator--dot {
          display: inline-block;
          position: absolute;
          top: 0;
          left: 0;
          height: 7px;
          width: 7px;
          border: 1px solid #000000;
          border-radius: 100%;
          background-color: #ffffff;

          &.first-indicator--dot {
            background-color: #000000;
          }
        }

        &:first-child {
          left: 0;
        }

        &:last-child {
          right: 0;
        }
      }
    }

    .gifts-wrapper {
      padding: 16px 0;
      position: relative;

      &.gifts-wrapper--swiper {
        width: calc(100% + 20px);
      }
    }

    .swiper-prev--arrow,
    .swiper-next--arrow {
      -webkit-appearance: none;
      -moz-appearance: none;
      border: none;
      padding: 0;
      height: 32px;
      width: 32px;
      cursor: pointer;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      border-radius: 100%;
      z-index: 2;
    }

    .swiper-prev--arrow {
      left: -12px;
      display: none;

      svg {
        transform: rotate(180deg);
      }
    }

    .swiper-next--arrow {
      right: 8px;
    }
  `;

    constructor() {
        super();

        // Initialise GWP promotions
        const gwpOPromotionJSON = JSON.parse(
            document.getElementById("gwpPromotionJSON").textContent,
        );
        if (gwpOPromotionJSON) {
            this.gwpPromotions = gwpOPromotionJSON.promotions.filter(
                (promotion: GWPPromotion) =>
                    promotion.type !== GWPTypes.spend_x_get_y,
            );
            this.sxgyPromotions = gwpOPromotionJSON.promotions.filter(
                (promotion: GWPPromotion) =>
                    promotion.type === GWPTypes.spend_x_get_y,
            );

            if (this.sxgyPromotions.length > 0) {
                this.sxgyPromotions.forEach((sxgyPromotion) =>
                    this.thresholds.push(sxgyPromotion.qualifier_cart_value),
                );
            }
        }

        // Handle spend X get Y promotions
        this._fetchCartData().then(() => {
            this._handleThresholds(this.thresholds);
        });
    }

    connectedCallback(): void {
        super.connectedCallback();
        register(); // Register swiper
    }

    updated(): void {
        if (this.hasFreeGift) {
            window.dispatchEvent(
                new CustomEvent("gwp-validator:has-free-gift", {
                    detail: [
                        {
                            hasFreeGift: this.hasFreeGift,
                        },
                    ],
                }),
            );
        }
    }

    private _slideNext(): void {
        const swiperEl = this.shadowRoot.querySelector(
            "swiper-container",
        ) as SwiperContainer;
        const nextArrowEl = this.shadowRoot.querySelector(
            ".swiper-next--arrow",
        ) as HTMLElement;
        const prevArrowEl = this.shadowRoot.querySelector(
            ".swiper-prev--arrow",
        ) as HTMLElement;

        if (swiperEl) {
            swiperEl.swiper.slideNext();

            if (prevArrowEl) {
                prevArrowEl.style.display = "block";
            }

            if (swiperEl.swiper.isEnd && nextArrowEl) {
                nextArrowEl.style.display = "none";
            }
        }
    }

    private _slidePrev(): void {
        const swiperEl = this.shadowRoot.querySelector(
            "swiper-container",
        ) as SwiperContainer;
        const nextArrowEl = this.shadowRoot.querySelector(
            ".swiper-next--arrow",
        ) as HTMLElement;
        const prevArrowEl = this.shadowRoot.querySelector(
            ".swiper-prev--arrow",
        ) as HTMLElement;

        if (swiperEl) {
            swiperEl.swiper.slidePrev();

            if (nextArrowEl) {
                nextArrowEl.style.display = "block";
            }

            if (swiperEl.swiper.isBeginning && prevArrowEl) {
                prevArrowEl.style.display = "none";
            }
        }
    }

    private _handleThresholds(thresholds: number[]) {
        const sortedThresholds: number[] = thresholds.sort((n1, n2) => n1 - n2);
        let nextValueFound = false;
        this.thresholds = [...new Set(sortedThresholds)];

        this.thresholds.forEach((threshold) => {
            if (this.cartTotalPrice < threshold && !nextValueFound) {
                this.valueUntilNextGift = threshold - this.cartTotalPrice;
                nextValueFound = true;
            }

            this.lastThreshold = threshold;
        });

        this.firstThreshold = thresholds[0];
    }

    private async _fetchCartData(): Promise<void> {
        const response = await fetch("/cart?view=ajax");
        this.cart = await response.json();

        const cartItems = this.cart.items.filter(
            (item) => !item.properties?.gwp,
        );
        this.cartTotalPrice =
            cartItems &&
            cartItems.reduce(
                (cartTotal, { final_line_price }) =>
                    cartTotal + final_line_price,
                0,
            );
    }

    private _validateGift(promotion: GWPPromotion): boolean {
        let qualifierProducts: CartItem[];
        let validatedGift = false;

        switch (promotion.type) {
            case GWPTypes.buy_x_get_y:
                qualifierProducts = this.cart?.items.filter(
                    (item) =>
                        item.tags?.includes(promotion.qualifier_product_tag) &&
                        !item.properties?.gwp,
                );

                validatedGift =
                    qualifierProducts &&
                    qualifierProducts.reduce(
                        (qualifierProductQuantity, { quantity }) =>
                            qualifierProductQuantity + quantity,
                        0,
                    ) >= promotion.qualifier_product_quantity;

                this._handlePromotion(promotion, validatedGift);

                return validatedGift;
            case GWPTypes.buy_x_of_y_get_z:
                qualifierProducts = this.cart?.items.filter(
                    (item) =>
                        item.collections?.includes(
                            promotion.qualifier_product_collection,
                        ) && !item.properties?.gwp,
                );

                validatedGift =
                    qualifierProducts &&
                    qualifierProducts.reduce(
                        (qualifierProductQuantity, { quantity }) =>
                            qualifierProductQuantity + quantity,
                        0,
                    ) >= promotion.qualifier_product_quantity;

                this._handlePromotion(promotion, validatedGift);

                return validatedGift;
            case GWPTypes.spend_x_get_y:
                qualifierProducts = this.cart?.items.filter(
                    (item) => !item.properties?.gwp,
                );

                validatedGift =
                    qualifierProducts &&
                    qualifierProducts.reduce(
                        (qualifierCartValue, { final_line_price }) =>
                            qualifierCartValue + final_line_price,
                        0,
                    ) >= promotion.qualifier_cart_value;

                if (validatedGift) {
                    this.hasFreeGift = true;
                } else if (this._giftAdded(promotion)) {
                    this._removeGift(promotion);
                }

                return validatedGift;
            case GWPTypes.spend_x_of_y_get_z:
                qualifierProducts = this.cart?.items.filter(
                    (item) =>
                        item.collections?.includes(
                            promotion.qualifier_product_collection,
                        ) && !item.properties?.gwp,
                );

                validatedGift =
                    qualifierProducts &&
                    qualifierProducts.reduce(
                        (qualifierCartValue, { final_line_price }) =>
                            qualifierCartValue + final_line_price,
                        0,
                    ) >= promotion.qualifier_cart_value;

                this._handlePromotion(promotion, validatedGift);

                return validatedGift;
        }
    }

    private _handlePromotion(promotion: GWPPromotion, validatedGift: boolean) {
        if (validatedGift) {
            this.hasFreeGift = true;
        } else if (this._giftAdded(promotion)) {
            this._removeGift(promotion);
        }

        if (promotion.hide_gwp_in_cart) {
            const promotionAccordion = this.shadowRoot.querySelector(
                `#promotion-${promotion.id}`,
            ) as HTMLElement;
            if (promotionAccordion) {
                promotionAccordion.style.display = validatedGift
                    ? "block"
                    : "none";
            }
        }
    }

    private _giftAdded(promotion: GWPPromotion): boolean {
        const gift = this.cart?.items.find(
            (item) =>
                item.variant_id === Number(promotion.gift_id) &&
                item.properties?.gwp,
        );
        return gift !== undefined;
    }

    private async _removeGift(promotion: GWPPromotion) {
        try {
            await CartJS.removeItemById(promotion.gift_id);
        } catch (err) {
            console.error(err);
        }
    }

    protected render(): TemplateResult {
        const sxgyGiftAdded =
            this.sxgyPromotions.length > 0 &&
            this.sxgyPromotions.some((sxgyPromotion) =>
                this._giftAdded(sxgyPromotion),
            );

        return html`
      ${this.gwpPromotions.map((promotion) => {
          const giftAdded = this._giftAdded(promotion);
          const giftValidated = this._validateGift(promotion);

          return giftValidated
              ? html`
          <details id="promotion-${promotion.id}" name="gwp-accordion-${promotion.id}" ?open=${!giftAdded}>
            <summary>
              <div class="accordion-header">
                <div class="heading-wrapper">
                  ${giftAdded ? html`<h4>${this.props.locales.gift_added_to_cart_heading} ${IconAdded(24, 24)}</h4>` : html`<h4>${this.props.locales.select_free_gift_heading}</h4>`}
                  <p class="gwp-description">${promotion.title}</p> 
                </div>
                
                <span class="icon-chevron">
                  ${IconChevronUp(12, 8, "icon-chevron--up")}
                  ${IconChevronDown(12, 8, "icon-chevron--down")}
                </span>
              </div>
            </summary>

            <div class="gifts-wrapper">
              <gwp-gift .image=${promotion.gift_image} .title=${promotion.gift_title} .enabled=${giftValidated} .added=${giftAdded} .variantId=${promotion.gift_id}></gwp-gift>
            </div>
          </details>
        `
              : null;
      })}

      ${
          this.sxgyPromotions.length > 0
              ? html`
          <details name="gwp-accordion-sxgy" ?open=${!sxgyGiftAdded}>
            <summary>
              <div class="accordion-header">
                ${sxgyGiftAdded ? html`<h4>${this.props.locales.gift_added_to_cart_heading} ${IconAdded(24, 24)}</h4>` : html`<h4>${this.cartTotalPrice >= this.firstThreshold ? this.props.locales.select_free_gift_heading : this.props.locales.free_gift_spend_amount_heading.replace("{{amount}}", `${this.valueUntilNextGift / 100}`)}</h4>`}
                
                <span class="icon-chevron">
                  ${IconChevronUp(12, 8, "icon-chevron--up")}
                  ${IconChevronDown(12, 8, "icon-chevron--down")}
                </span>
              </div>
              
              <div class="gwp-range">
                <p class="gwp-description">
                  ${
                      this.cartTotalPrice >= this.lastThreshold
                          ? this.props.locales.choose_free_gift_heading
                          : html`${this.cartTotalPrice >= this.firstThreshold ? this.props.locales.free_gift_spend_amount_remain.replace("{{amount}}", `${this.valueUntilNextGift / 100}`) : this.props.locales.free_gift_spend_amount_full.replace("{{amount}}", `${this.valueUntilNextGift / 100}`)}`
                  }
                </h5>

                <div class="progress-wrapper">
                  <progress
                    value=${this.cartTotalPrice}
                    max=${this.lastThreshold}
                    class="threshold-block"
                  ></progress>
                </div>

                <div class="progress-indicators">
                  <span class="progress-indicator">
                    <span class="${this.cart?.total_price <= 0 ? "first-indicator--dot " : ""}progress-indicator--dot"></span>
                  </span>
                  ${this.thresholds.map((threshold) =>
                      this.lastThreshold === threshold
                          ? html`<span class="progress-indicator"><span class="progress-indicator--dot"></span></span>`
                          : html`<span class="progress-indicator" style="left: ${(threshold * 100) / this.lastThreshold}%"><span class="progress-indicator--dot"></span></span>`,
                  )}
                </div>
              </div>
            </summary>

            <div class="gifts-wrapper gifts-wrapper--swiper">
              <button class="swiper-prev--arrow" @click=${this._slidePrev}>
                ${IconSwiperArrow(32, 32)}
              </button>
              <swiper-container slides-per-view="1.5" space-between="16">
                ${this.sxgyPromotions.map(
                    (promotion) => html`
                  <swiper-slide>
                    <gwp-gift .image=${promotion.gift_image} .title=${promotion.gift_title} .enabled=${this._validateGift(promotion)} .added=${this._giftAdded(promotion)} .variantId=${promotion.gift_id}></gwp-gift>
                  </swiper-slide>
                `,
                )}
              </swiper-container>
              <button class="swiper-next--arrow" @click=${this._slideNext}>
                ${IconSwiperArrow(32, 32)}
              </button>
            </div>
          </details>
        `
              : null
      }
    `;
    }
}

customElements.get("gwp-selector") ||
    customElements.define("gwp-selector", GWPSelector);

class GWPGift extends LitElement {
    @property({ type: Boolean, reflect: true })
    public enabled!: boolean;

    @property({ type: Boolean, reflect: true })
    public added!: boolean;

    @property({ type: String })
    public title!: string;

    @property({ type: String })
    public image?: string;

    @property({ type: String })
    public variantId?: string;

    static styles: CSSResult = css`
    .gift-item {
      display: flex;
      flex: 1 1 0;
      align-items: center;
      border: 1px solid #F5F5F5;
      border-radius: 6px;
      background: #ffffff;
      max-width: 360px;

      .gift-item--inner {
        display: flex;
        width: 100%;
        padding: 8px;
        gap: 14px;
        position: relative;

        img {
          border-radius: 6px;
        }

        .icon-lock {
          position: absolute;
          top: 5px;
          left: 5px;
        }
      }

      &.disabled {
        background: #D7D7D7;
      }
    }

    .inner-content--container {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: space-between;
      row-gap: 8px;
      width: 100%;

      h5 {
        font-size: 16px;
        line-height: 130%;
        font-weight: 400;
        letter-spacing: 0;
        text-transform: capitalize;
        margin: 0;
      }

      button {
        display: flex;
        align-items: center;
        justify-content: center;
        column-gap: 4px;
        width: 100%;
        height: 40px;
        border: 1px solid #000000;
        color: #000000;
        background: transparent;
        cursor: pointer;
        font-size: 14px;
        font-weight: 500;
        line-height: 140%;
      }

      button[disabled] {
        cursor: not-allowed;
        opacity: 60%;
      }
    }
  `;

    private async _handleClick() {
        if (this.variantId) {
            try {
                await CartJS.addItem(this.variantId, 1, {
                    gwp: "Free Gift", // Custom property to validate gifts
                });
            } catch (err) {
                console.error(err);
            }
        }
    }

    protected render(): TemplateResult {
        return html`
      <div class="gift-item ${!this.enabled ? "disabled" : ""}">
        <div class="gift-item--inner">
          <img
            src="${this.image || ""}"
            alt=""
            loading="lazy"
            width="77"
            height="92"
          />

          ${!this.enabled ? html`<span class="icon-lock">${IconLock(32, 32)}</span>` : null}

          <div class="inner-content--container">
            <h5>${this.title}</h5>
            <button type="button" ?disabled=${!this.enabled || this.added} @click=${this._handleClick}>
              ${this.added ? IconAdded(17, 16) : null}
              ${this.added ? "Added" : "Add"}
            </button>
          </div>
        </div>
      </div>
    `;
    }
}

customElements.get("gwp-gift") || customElements.define("gwp-gift", GWPGift);
